<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { apiRequest, assertOk } from '../lib/apiClient'
import { useAuthStore } from '../stores/auth'
import { useLoginModalStore } from '../stores/loginModal'

const auth = useAuthStore()
const login = useLoginModalStore()
const router = useRouter()

const loginTab = ref<'sms' | 'email' | 'wechat'>('sms')
const loginBusy = ref(false)
const loginError = ref('')
const phoneNumber = ref('')
const smsCode = ref('')
const email = ref('')
const emailCode = ref('')
const inviteCode = ref('')
const smsCountdown = ref(0)
const smsTimer = ref<number | null>(null)
const smsVerificationInfo = ref<any>(null)
const emailCountdown = ref(0)
const emailTimer = ref<number | null>(null)
const emailVerificationInfo = ref<any>(null)
const wxAppId = (import.meta.env.VITE_WX_APPID as string | undefined) || ''
const captchaDisableEncrypt = String((import.meta.env as any).VITE_CAPTCHA_DISABLE_ENCRYPT || '').trim() === '1'
const captchaScriptUrl =
  String((import.meta.env as any).VITE_TCAPTCHA_SCRIPT_URL || '').trim() || 'https://ca.turing.captcha.qcloud.com/TJNCaptcha-global.js'

const wxQrUri = ref('')
const wxQrBusy = ref(false)
const wxQrError = ref('')
const wxState = ref('')
const wxRedirectBase = ref('')
const wxIframeEl = ref<HTMLIFrameElement | null>(null)
const WX_OPEN_STATE_KEY = 'wx_open_state'

type SmsCaptchaConfig = {
  CaptchaAppId?: string
  aidEncrypted?: string
  aidEncryptedType?: string
  expiresIn?: number
}

let captchaScriptPromise: Promise<void> | null = null
let captchaMountEl: HTMLDivElement | null = null

function getCaptchaMountEl() {
  if (captchaMountEl && document.body.contains(captchaMountEl)) return captchaMountEl
  const el = document.createElement('div')
  el.style.position = 'fixed'
  el.style.left = '-9999px'
  el.style.top = '-9999px'
  el.style.width = '1px'
  el.style.height = '1px'
  el.style.opacity = '0'
  document.body.appendChild(el)
  captchaMountEl = el
  return el
}

async function ensureTencentCaptcha() {
  if (typeof window === 'undefined') throw new Error('验证码不可用')
  if ((window as any).TencentCaptcha) return
  if (captchaScriptPromise) return await captchaScriptPromise
  captchaScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector('script[data-tcaptcha="1"]') as HTMLScriptElement | null
    if (existing && (window as any).TencentCaptcha) {
      resolve()
      return
    }
    const s = document.createElement('script')
    s.setAttribute('data-tcaptcha', '1')
    s.async = true
    s.src = captchaScriptUrl
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('验证码加载失败，请稍后重试'))
    document.head.appendChild(s)
  })
  try {
    await captchaScriptPromise
  } finally {
    captchaScriptPromise = null
  }
}

function normalizeChinaPhoneDigits(raw: string) {
  const digits = String(raw || '').replace(/[^\d]/g, '')
  if (digits.startsWith('86') && digits.length === 13) return digits.slice(2)
  return digits
}

async function verifySmsCaptcha(phoneNumberRaw: string) {
  const phoneDigits = normalizeChinaPhoneDigits(phoneNumberRaw)
  if (!phoneDigits) throw new Error('请输入手机号')
  const cfgResp = await apiRequest<SmsCaptchaConfig>('/sms-auth?action=config', { method: 'GET' })
  const cfg = assertOk(cfgResp, '获取图形验证码配置失败')
  const appId = String((cfg as any)?.CaptchaAppId || '').trim()
  if (!appId) throw new Error('获取图形验证码配置失败')
  await ensureTencentCaptcha()
  const result = await new Promise<{ ticket: string; randstr: string }>((resolve, reject) => {
    const CaptchaCtor: any = (window as any).TencentCaptcha
    if (!CaptchaCtor) {
      reject(new Error('验证码加载失败，请稍后重试'))
      return
    }
    const mount = getCaptchaMountEl()
    let done = false
    let inst: any = null
    const finish = (err: Error | null, data?: { ticket: string; randstr: string }) => {
      if (done) return
      done = true
      try {
        inst?.destroy?.()
      } catch {}
      if (err) reject(err)
      else if (data) resolve(data)
      else reject(new Error('图形验证码验证失败'))
    }
    try {
      const cb = (res: any) => {
        const ret = Number(res?.ret)
        if (ret === 0 && res?.ticket && res?.randstr) {
          finish(null, { ticket: String(res.ticket), randstr: String(res.randstr) })
          return
        }
        if (ret === 2) {
          finish(new Error('已取消验证'))
          return
        }
        const msg = String(res?.errorMessage || '').trim()
        finish(new Error(msg || '图形验证码验证失败'))
      }
      const opts = {
        aidEncrypted: captchaDisableEncrypt ? undefined : String((cfg as any)?.aidEncrypted || '').trim() || undefined,
        aidEncryptedType: captchaDisableEncrypt ? undefined : String((cfg as any)?.aidEncryptedType || '').trim() || undefined,
        userLanguage: 'zh-cn',
        type: 'popup',
        bizState: phoneDigits,
      }
      try {
        inst = new CaptchaCtor(mount, appId, cb, opts)
      } catch {
        inst = new CaptchaCtor(appId, cb, opts)
      }
      inst.show()
    } catch {
      finish(new Error('图形验证码验证失败'))
    }
  })
  const verifyResp = await apiRequest<{ expiresIn?: number }>(`/sms-auth?action=verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ phone: phoneDigits, ticket: result.ticket, randstr: result.randstr }),
  })
  assertOk(verifyResp, '图形验证码验证失败')
}

function stopSmsTimer() {
  if (smsTimer.value) {
    window.clearInterval(smsTimer.value)
    smsTimer.value = null
  }
}

function stopEmailTimer() {
  if (emailTimer.value) {
    window.clearInterval(emailTimer.value)
    emailTimer.value = null
  }
}

function resetState() {
  loginTab.value = login.tab || 'sms'
  loginBusy.value = false
  loginError.value = ''
  email.value = ''
  emailCode.value = ''
  smsCode.value = ''
  inviteCode.value = ''
  smsCountdown.value = 0
  smsVerificationInfo.value = null
  stopSmsTimer()
  emailCountdown.value = 0
  emailVerificationInfo.value = null
  stopEmailTimer()
  wxQrUri.value = ''
  wxQrBusy.value = false
  wxQrError.value = ''
  wxState.value = ''
  wxRedirectBase.value = ''
}

async function closeLogin() {
  const cancel = String(login.cancelPath || '').trim()
  login.close()
  resetState()
  if (cancel) {
    try {
      await router.replace(cancel)
    } catch {
      try {
        window.location.href = cancel
      } catch {}
    }
  }
}

function closeLoginSilently() {
  login.close()
  resetState()
}

async function onLoginSuccess() {
  const next = String(login.nextPath || '').trim()
  closeLoginSilently()
  if (next) {
    try {
      await router.push(next)
    } catch {}
  }
}

watch(
  () => login.open,
  (open) => {
    if (!open) {
      resetState()
      return
    }
    resetState()
  },
)

watch(
  () => [login.open, loginTab.value] as const,
  ([open, tab]) => {
    if (!open) return
    if (tab === 'wechat') {
      if (!wxQrUri.value && !wxQrBusy.value) void prepareWeChatQr()
      return
    }
    wxQrUri.value = ''
    wxQrBusy.value = false
    wxQrError.value = ''
    wxState.value = ''
    wxRedirectBase.value = ''
  },
)

onBeforeUnmount(() => {
  stopSmsTimer()
  stopEmailTimer()
})

async function attachInvitation(code: string) {
  const invitationCode = String(code || '').trim()
  if (!invitationCode) return
  if (!auth.token) return
  try {
    const resp = await apiRequest<any>('/user/attachInvitation', {
      method: 'POST',
      token: auth.token,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invitation_code: invitationCode }),
    } as any)
    if (Number((resp as any)?.code) === 0) {
      try {
        await auth.fetchMe()
      } catch {}
    }
  } catch {}
}

function formatLoginError(e: unknown): string {
  if (!e) return '登录失败'
  if (e instanceof Error) {
    const anyE: any = e as any
    const desc = String(anyE?.error_description || anyE?.message || '').trim().toLowerCase()
    const errStr = String(anyE?.error || anyE?.code || '').trim().toLowerCase()
    const errCode = anyE?.error_code ?? anyE?.errorCode
    const status = anyE?.status ?? anyE?.http_status ?? anyE?.httpStatus
    const isSystemBusy =
      (String(anyE?.code || '').trim().toUpperCase() === 'UNAVAILABLE' || errStr === 'unavailable' || Number(errCode) === 14) &&
      (Number(status) === 503 ||
        desc.includes('context deadline exceeded') ||
        desc.includes('client.timeout exceeded') ||
        desc.includes('timeout exceeded'))
    if (isSystemBusy) return '当前系统繁忙，请等待10秒后，重新尝试发送验证码。'
    return e.message || '登录失败'
  }
  if (typeof e === 'string') {
    const desc = String(e || '').trim().toLowerCase()
    const isSystemBusy =
      desc.includes('context deadline exceeded') || desc.includes('client.timeout exceeded') || desc.includes('timeout exceeded')
    if (isSystemBusy) return '当前系统繁忙，请等待10秒后，重新尝试发送验证码。'
    return e
  }
  if (typeof e === 'object') {
    const anyE: any = e as any
    const desc = String(anyE?.error_description || anyE?.message || '').trim().toLowerCase()
    const errStr = String(anyE?.error || anyE?.code || '').trim().toLowerCase()
    const errCode = anyE?.error_code ?? anyE?.errorCode
    const status = anyE?.status ?? anyE?.http_status ?? anyE?.httpStatus
    const isSystemBusy =
      (String(anyE?.code || '').trim().toUpperCase() === 'UNAVAILABLE' || errStr === 'unavailable' || Number(errCode) === 14) &&
      (Number(status) === 503 ||
        desc.includes('context deadline exceeded') ||
        desc.includes('client.timeout exceeded') ||
        desc.includes('timeout exceeded'))
    if (isSystemBusy) return '当前系统繁忙，请等待10秒后，重新尝试发送验证码。'
    const msg =
      String(anyE?.message || '').trim() ||
      String(anyE?.error_description || '').trim() ||
      String(anyE?.error || '').trim() ||
      String(anyE?.msg || '').trim()
    const code = anyE?.code ?? anyE?.status ?? anyE?.error_code
    if (msg && code !== undefined && code !== null && String(code).trim()) return `${msg}（${String(code)}）`
    if (msg) return msg
    try {
      return JSON.stringify(e)
    } catch {}
  }
  return String(e)
}

function normalizePhoneNumber(raw: string) {
  const s = String(raw || '').trim()
  if (!s) return ''
  if (s.startsWith('+')) return s
  const digits = s.replace(/[^\d]/g, '')
  if (!digits) return ''
  return `+86 ${digits}`
}

async function sendSms() {
  const phone = normalizePhoneNumber(phoneNumber.value)
  if (!phone) {
    loginError.value = '请输入手机号'
    return
  }
  if (smsCountdown.value > 0) return
  loginBusy.value = true
  loginError.value = ''
  try {
    await verifySmsCaptcha(phone)
    smsVerificationInfo.value = await auth.sendSmsCode(phone)
    smsCountdown.value = 60
    stopSmsTimer()
    smsTimer.value = window.setInterval(() => {
      smsCountdown.value = Math.max(0, smsCountdown.value - 1)
      if (smsCountdown.value <= 0) stopSmsTimer()
    }, 1000)
  } catch (e) {
    loginError.value = formatLoginError(e)
  } finally {
    loginBusy.value = false
  }
}

async function sendEmail() {
  if (!email.value.trim()) {
    loginError.value = '请输入邮箱'
    return
  }
  if (emailCountdown.value > 0) return
  loginBusy.value = true
  loginError.value = ''
  try {
    emailVerificationInfo.value = await auth.sendEmailCode(email.value.trim())
    emailCountdown.value = 60
    stopEmailTimer()
    emailTimer.value = window.setInterval(() => {
      emailCountdown.value = Math.max(0, emailCountdown.value - 1)
      if (emailCountdown.value <= 0) stopEmailTimer()
    }, 1000)
  } catch (e) {
    loginError.value = formatLoginError(e)
  } finally {
    loginBusy.value = false
  }
}

async function loginWithSms() {
  const phone = normalizePhoneNumber(phoneNumber.value)
  if (!phone) {
    loginError.value = '请输入手机号'
    return
  }
  if (!smsCode.value.trim()) {
    loginError.value = '请输入短信验证码'
    return
  }
  if (!smsVerificationInfo.value) {
    loginError.value = '请先发送验证码'
    return
  }
  loginBusy.value = true
  loginError.value = ''
  try {
    await auth.signInWithSms(phone, smsCode.value.trim(), smsVerificationInfo.value)
    await attachInvitation(inviteCode.value.trim() || login.defaultInvitationCode)
    try {
      await auth.bindPendingWeChatOpenProvider()
    } catch {}
    await onLoginSuccess()
  } catch (e) {
    loginError.value = formatLoginError(e)
  } finally {
    loginBusy.value = false
  }
}

async function loginWithEmail() {
  if (!email.value.trim()) {
    loginError.value = '请输入邮箱'
    return
  }
  if (!emailCode.value.trim()) {
    loginError.value = '请输入邮箱验证码'
    return
  }
  if (!emailVerificationInfo.value) {
    loginError.value = '请先发送验证码'
    return
  }
  loginBusy.value = true
  loginError.value = ''
  try {
    await auth.signInWithEmail(email.value.trim(), emailCode.value.trim(), emailVerificationInfo.value)
    await attachInvitation(inviteCode.value.trim() || login.defaultInvitationCode)
    try {
      await auth.bindPendingWeChatOpenProvider()
    } catch {}
    await onLoginSuccess()
  } catch (e) {
    loginError.value = formatLoginError(e)
  } finally {
    loginBusy.value = false
  }
}

async function loginWithWeChat() {
  if (!wxAppId) {
    loginError.value = '微信登陆访问异常，请联系客服'
    return
  }
  loginBusy.value = true
  loginError.value = ''
  try {
    if (!wxQrUri.value) await prepareWeChatQr()
  } catch (e) {
    loginError.value = formatLoginError(e)
    loginBusy.value = false
  }
}

function buildWxRedirectBase() {
  const u = new URL(window.location.href)
  u.hash = ''
  u.search = ''
  const host = u.hostname.toLowerCase()
  const needsCanonical = host !== 'www.wuhenai.com' || Boolean(u.port)
  if (needsCanonical) {
    u.protocol = 'https:'
    u.hostname = 'www.wuhenai.com'
    u.port = ''
  }
  return u.toString()
}

function createWxState() {
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    const buf = new Uint8Array(8)
    crypto.getRandomValues(buf)
    return (
      'wx_open_' +
      Array.from(buf)
        .map((x) => x.toString(16).padStart(2, '0'))
        .join('')
    )
  }
  return `wx_open_${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`
}

async function prepareWeChatQr() {
  if (!wxAppId) {
    wxQrError.value = '缺少微信配置：请提供 VITE_WX_APPID'
    return
  }
  wxQrBusy.value = true
  wxQrError.value = ''
  wxQrUri.value = ''
  try {
    const redirectBase = buildWxRedirectBase()
    const state = createWxState()
    wxRedirectBase.value = redirectBase
    wxState.value = state
    try {
      window.sessionStorage.setItem(WX_OPEN_STATE_KEY, state)
    } catch {}
    wxQrUri.value = await auth.genWeChatOpenQrUri(redirectBase, state)
  } catch (e) {
    wxQrError.value = e instanceof Error ? e.message : String(e)
  } finally {
    wxQrBusy.value = false
  }
}

async function handleWeChatOpenRedirectFromLocation() {
  let u: URL | null = null
  let state = ''
  let shouldCleanup = false
  try {
    u = new URL(window.location.href)
    const code = String(u.searchParams.get('code') || '').trim()
    state = String(u.searchParams.get('state') || '').trim()
    if (!code || !state) return false
    const expected = (() => {
      if (wxState.value) return wxState.value
      try {
        return window.sessionStorage.getItem(WX_OPEN_STATE_KEY) || ''
      } catch {
        return ''
      }
    })()
    if (expected && state !== expected) return false
    shouldCleanup = true
    login.show({ tab: 'wechat', defaultInvitationCode: login.defaultInvitationCode, nextPath: login.nextPath })
    loginTab.value = 'wechat'
    loginBusy.value = true
    loginError.value = ''
    try {
      await auth.signInWithWeChatOpenCode(window.location.href, code)
      try {
        await attachInvitation(login.defaultInvitationCode)
      } catch {}
      await onLoginSuccess()
      return true
    } catch (e) {
      const msg = formatLoginError(e)
      loginError.value = msg
      if (msg.includes('绑定') || msg.includes('短信')) loginTab.value = 'sms'
      return false
    }
  } catch {
    return false
  } finally {
    loginBusy.value = false
    if (shouldCleanup) {
      try {
        if (u) {
          u.searchParams.delete('code')
          u.searchParams.delete('state')
          window.history.replaceState({}, '', u.toString())
        }
      } catch {}
      try {
        window.sessionStorage.removeItem(WX_OPEN_STATE_KEY)
      } catch {}
    }
  }
}

async function onWxIframeLoad() {
  const el = wxIframeEl.value
  if (!el) return
  try {
    const href = String(el.contentWindow?.location?.href || '')
    if (!href) return
    const u = new URL(href)
    const code = String(u.searchParams.get('code') || '').trim()
    const state = String(u.searchParams.get('state') || '').trim()
    if (!code || !state) return
    const expected = (() => {
      if (wxState.value) return wxState.value
      try {
        return window.sessionStorage.getItem(WX_OPEN_STATE_KEY) || ''
      } catch {
        return ''
      }
    })()
    if (expected && state !== expected) return
    loginBusy.value = true
    loginError.value = ''
    try {
      await auth.signInWithWeChatOpenCode(href, code)
      try {
        await attachInvitation(login.defaultInvitationCode)
      } catch {}
      await onLoginSuccess()
    } catch (e) {
      const msg = formatLoginError(e)
      loginError.value = msg
      if (msg.includes('绑定') || msg.includes('短信')) loginTab.value = 'sms'
    } finally {
      loginBusy.value = false
      try {
        el.src = 'about:blank'
      } catch {}
      wxQrUri.value = ''
      try {
        window.sessionStorage.removeItem(WX_OPEN_STATE_KEY)
      } catch {}
    }
  } catch {}
}

onMounted(() => {
  if (typeof window === 'undefined') return
  void handleWeChatOpenRedirectFromLocation()
})
</script>

<template>
  <div v-if="login.open" class="modalOverlay" role="dialog" aria-modal="true" aria-label="登录" style="display: flex" @click.self="closeLogin">
    <div class="modal modalLogin">
      <div class="modalHeader">
        <strong>注册与登陆</strong>
        <button class="iconBtn" type="button" aria-label="关闭" @click="closeLogin">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M18 6 6 18"></path>
            <path d="M6 6l12 12"></path>
          </svg>
        </button>
      </div>
      <div class="modalBody">
        <div class="tabBar" aria-label="登录方式">
          <button class="tab" type="button" :aria-pressed="loginTab === 'sms'" @click="loginTab = 'sms'">短信验证码</button>
          <button class="tab" type="button" :aria-pressed="loginTab === 'email'" @click="loginTab = 'email'">邮箱验证码</button>
          <button class="tab" type="button" :aria-pressed="loginTab === 'wechat'" @click="loginTab = 'wechat'">微信扫码</button>
        </div>

        <div v-if="loginError" class="miniCard">
          <h4>登录失败</h4>
          <p>{{ loginError }}</p>
        </div>

        <template v-if="loginTab === 'sms'">
          <div class="field">
            <div style="display: flex; gap: 10px; align-items: center">
              <select
                disabled
                aria-label="国家码"
                style="
                  width: 92px;
                  padding-right: 32px;
                  appearance: none;
                  -webkit-appearance: none;
                  -moz-appearance: none;
                  background-image: url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22rgba(255%2C255%2C255%2C0.72)%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22/%3E%3C/svg%3E');
                  background-repeat: no-repeat;
                  background-size: 14px 14px;
                  background-position: calc(100% - 14px) 50%;
                "
              >
                <option value="+86">+86</option>
              </select>
              <input v-model="phoneNumber" type="text" placeholder="输入手机号（仅限中国大陆）" autocomplete="tel" aria-label="手机号" />
            </div>
          </div>

          <div class="field">
            <div class="codeWrap">
              <input v-model="smsCode" type="text" placeholder="输入验证码" inputmode="numeric" aria-label="验证码" />
              <button class="codeSendBtn" type="button" :disabled="loginBusy || smsCountdown > 0" @click="sendSms">
                {{ smsCountdown > 0 ? `已发送（${smsCountdown}s）` : '发送验证码' }}
              </button>
            </div>
          </div>

          <div class="field">
            <input v-model="inviteCode" type="text" placeholder="输入邀请码，可不填。首次登陆时有效" autocomplete="off" aria-label="邀请码" />
            <div style="margin-top: 6px; font-size: 12px; color: rgba(255, 255, 255, 0.76); font-weight: 700">验证码发送自【杭州岁羽网络】</div>
          </div>

          <div class="loginBtnRow">
            <button class="cta loginBtn" type="button" :disabled="loginBusy" @click="loginWithSms">登录</button>
          </div>
        </template>

        <template v-else-if="loginTab === 'email'">
          <div class="field">
            <input v-model="email" type="text" inputmode="email" placeholder="请输入邮箱" autocomplete="email" aria-label="邮箱" />
          </div>

          <div class="field">
            <div class="codeWrap">
              <input v-model="emailCode" type="text" placeholder="请输入验证码" inputmode="numeric" aria-label="验证码" />
              <button class="codeSendBtn" type="button" :disabled="loginBusy || emailCountdown > 0" @click="sendEmail">
                {{ emailCountdown > 0 ? `已发送（${emailCountdown}s）` : '发送验证码' }}
              </button>
            </div>
          </div>

          <div class="field">
            <input v-model="inviteCode" type="text" placeholder="输入邀请码，可不填。首次登陆时有效" autocomplete="off" aria-label="邀请码" />
            <div style="margin-top: 6px; font-size: 12px; color: rgba(255, 255, 255, 0.76); font-weight: 700">验证码发送自【云开发CloudBase】</div>
          </div>

          <div class="loginBtnRow">
            <button class="cta loginBtn" type="button" :disabled="loginBusy" @click="loginWithEmail">登录</button>
          </div>
        </template>

        <template v-else>
          <div v-if="wxQrError" class="miniCard">
            <h4>微信登录不可用</h4>
            <p>{{ wxQrError }}</p>
          </div>
          <div v-else class="miniCard wxQrCard">
            <div v-if="wxQrBusy" style="text-align: center">二维码加载中…</div>
            <div v-else-if="wxQrUri" class="wxQrFrameWrap" aria-label="微信二维码区域">
              <iframe ref="wxIframeEl" :src="wxQrUri" title="微信扫码登录" class="wxQrFrame" @load="onWxIframeLoad" />
            </div>
            <div v-else style="display: flex; justify-content: center">
              <button class="cta loginBtn" type="button" :disabled="loginBusy" @click="loginWithWeChat">获取二维码</button>
            </div>
          </div>
          <div style="display: flex; justify-content: center; margin-top: 10px; flex-direction: column; align-items: center; gap: 10px">
            <span class="pill ok" style="text-align: center; font-size: 14px; font-weight: 900; padding: 8px 12px">
              首次登录，请使用短信验证码或邮箱验证码
            </span>
            <div style="text-align: center; font-size: 12px; color: rgba(255, 255, 255, 0.76); font-weight: 700">
              在账号管理页面绑定微信后，可使用微信登录
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
